import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
    createFailure,
    createListTableColumns,
    createValidationResult,
    deduplicateByProperty,
    depascalize,
    FailureSeverity,
    FormDialog,
    FormFields,
    FormTextField,
    FormValidationSummary,
    GridColumn,
    NamedProperty,
    renderDate,
    renderName,
    ShimmeredDetailsList,
    useNotifications,
    ValidationFailure,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import { DeliveryEndpointType } from "../../../../common/types/DeliveryEndpointType";
import { AuthContext } from "../../../../core/security/AuthProvider";
import { OrderStatus } from "../../../common/types/OrderStatus";
import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { OrderListingContext } from "../context/OrderListingContext";
import { isOrderApprovable } from "./ApproveOrderDeliveryListingDialog";

interface ApproveOrderDeliveryItem {
    id: string;
    content: string;
    poNumber: string;
    targetFileName: string;
    system: NamedProperty;
    startDate: string;
    deliveryFolder?: string;
    deliveryEndpointId: string;
    deliveryEndpointType: DeliveryEndpointType;
    targetPath: string;
}

interface ApproveOrderDeliveriesInputs {
    deliveryPath?: string;
}

interface ApproveOrderDeliveriesDialogProps {
    isOpen: boolean;
    onClose(): void;
}

export const ApproveOrderDeliveriesDialog: React.VFC<ApproveOrderDeliveriesDialogProps> = ({ isOpen, onClose }) => {
    const { approveOrderDeliveries, validateDeliveryPath } = useOrdersModuleApi();

    const { selectedItems, approveDeliveries: approveDeliveriesInContext } = useContext(OrderListingContext);

    const { success, error } = useNotifications();
    const { isAdmin } = useContext(AuthContext);

    const [orderDeliveries, setOrderDeliveries] = useState<ApproveOrderDeliveryItem[]>([]);

    const { control, formState, errors, handleSubmit } = useForm<ApproveOrderDeliveriesInputs>({
        defaultValues: {
            deliveryPath: ""
        },
        reValidateMode: "onSubmit",
        criteriaMode: "all"
    });

    const [validationResult, setValidationResult] = useState<ValidationResult>();

    const formHasErrors = validationResult?.failures.some((item) => item.severity === FailureSeverity.Error) ?? false;
    const areValidDeliveriesEmpty = !orderDeliveries.length;

    const reqiresDeliveryFolder = orderDeliveries.some(
        (orderDelivery) =>
            orderDelivery.deliveryEndpointType === DeliveryEndpointType.Smartjog ||
            orderDelivery.deliveryEndpointType === DeliveryEndpointType.Signiant
    );

    const hasSigniantDeliveries = orderDeliveries.some(
        (orderDelivery) => orderDelivery.deliveryEndpointType === DeliveryEndpointType.Signiant
    );

    useEffect(() => {
        if (selectedItems.length) {
            const newOrderDeliveries = selectedItems
                .filter(isOrderApprovable)
                .map<ApproveOrderDeliveryItem>((item) => ({
                    id: item.id,
                    content: item.content,
                    poNumber: item.pONumber,
                    targetFileName: item.targetFilename,
                    system: item.system,
                    startDate: item.startDate,
                    deliveryEndpointId: item.deliveryEndpointId,
                    deliveryEndpointType: item.deliveryEndpointType,
                    deliveryFolder: item.deliveryFolder,
                    targetPath: item.deliveryFolder
                        ? `${item.deliveryFolder}/${item.targetFilename}`
                        : item.targetFilename
                }));

            setOrderDeliveries(newOrderDeliveries);

            const result = validateOrderDeliveries(newOrderDeliveries, selectedItems.length);

            setValidationResult(result);
        }
    }, [selectedItems]);

    const handleDelivery = async ({ deliveryPath }: ApproveOrderDeliveriesInputs): Promise<void> => {
        try {
            await approveOrderDeliveries({
                deliveryPath,
                orderIds: orderDeliveries.map((item) => item.id)
            });

            approveAllInContextAndClose();

            success("Selected deliveries approved");
        } catch {
            error("Deliveries approve failed");
        }
    };

    const validateDeliveryPathUnique = async (deliveryPath: string): Promise<ValidationResult> => {
        if (!deliveryPath || !deliveryPathRegex.test(deliveryPath)) {
            return validationSuccessful();
        }

        if (hasSigniantDeliveries) return validationSuccessful();
        else return await validateDeliveryPath(deliveryPath, isAdmin);
    };

    const approveAllInContextAndClose = () => {
        approveDeliveriesInContext(orderDeliveries.map(({ id }) => id));

        handleClose();
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <FormDialog
            title="Approve deliveries"
            text="Are you sure you want to deliver listed orders?"
            formState={formState}
            hidden={!isOpen}
            isBlocking={formState.isValidating || formState.isSubmitting}
            width={"65%"}
            contentHeight={500}
            submit={{ onClick: handleSubmit(handleDelivery), disabled: areValidDeliveriesEmpty || formHasErrors }}
            cancel={{ onClick: handleClose }}
        >
            <FormFields>
                <FormValidationSummary validationResult={validationResult} />
                {reqiresDeliveryFolder && (
                    <GridColumn sm={4}>
                        <FormTextField
                            label="Delivery path"
                            name="deliveryPath"
                            placeholder="/delivery/path"
                            control={control}
                            errors={errors}
                            rules={{
                                required: "Enter delivery path",
                                pattern: deliveryPathRegex,
                                validate: validateDeliveryPathUnique
                            }}
                            disabled={areValidDeliveriesEmpty}
                        />
                    </GridColumn>
                )}
                <DeliveriesSummary deliveries={orderDeliveries} />
            </FormFields>
        </FormDialog>
    );
};

const deliveryPathRegex = /^(\/[\w-]+)+$/;

const processingCompletedStr = depascalize(OrderStatus[OrderStatus.Encoded]);
const deliveryFailedStr = depascalize(OrderStatus[OrderStatus.DeliveryFailed]);
const discardedOrdersInfoMessage = `Orders with status other than '${processingCompletedStr}', '${deliveryFailedStr}' and these delivered through Aspera with missing delivery folder, have been discarded`;

interface DeliveriesSummaryProps {
    deliveries: ApproveOrderDeliveryItem[];
}

const DeliveriesSummary: React.VFC<DeliveriesSummaryProps> = ({ deliveries }: DeliveriesSummaryProps) => {
    const columns = createListTableColumns<ApproveOrderDeliveryItem>([
        {
            type: "basic",
            key: "poNumber",
            name: "PO number",
            minWidth: 80,
            maxWidth: 250
        },
        {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 60,
            maxWidth: 300
        },
        {
            type: "basic",
            key: "targetPath",
            name: "Target path",
            minWidth: 110,
            maxWidth: 300
        },
        {
            type: "basic",
            key: "system",
            name: "System",
            minWidth: 80,
            maxWidth: 100,
            onRender: renderName
        },
        {
            type: "basic",
            key: "startDate",
            name: "Start date",
            minWidth: 70,
            maxWidth: 70,
            isResizable: false,
            onRender: renderDate
        }
    ]);

    return <ShimmeredDetailsList columns={columns} items={deliveries} layoutMode="justified" />;
};

const validateOrderDeliveries = (items: ApproveOrderDeliveryItem[], selectedItemsCount: number): ValidationResult => {
    return createValidationResult([
        validateSystems(items),
        validateTargetPaths(items),
        validateStartDates(items),
        validateDiscardedItems(items, selectedItemsCount),
        validateDeliveryEndpoints(items)
    ]);
};

const validateSystems = (items: ApproveOrderDeliveryItem[]): ValidationFailure | void => {
    const systems = items.map((item) => item.system);
    const uniqueSystems = deduplicateByProperty(systems, "id");

    if (uniqueSystems.length > 1) {
        return createFailure(`Systems vary between orders`, "different-systems", FailureSeverity.Warning);
    }
};

const validateTargetPaths = (items: ApproveOrderDeliveryItem[]): ValidationFailure | void => {
    const targetPaths = items.map((item) => {
        if (item.deliveryEndpointType === DeliveryEndpointType.Aspera) {
            return `${item.deliveryFolder}/${item.targetFileName}`;
        }

        return item.targetFileName;
    });

    const uniqueTargetPaths = Array.from(new Set(targetPaths));

    if (uniqueTargetPaths.length !== targetPaths.length) {
        return createFailure(
            `Target paths are duplicated between orders`,
            "duplicated-target-paths",
            FailureSeverity.Error
        );
    }
};

const validateStartDates = (items: ApproveOrderDeliveryItem[]): ValidationFailure | void => {
    const startDates = items.map((item) => item.startDate.split("T")[0]);
    const uniqueStartDates = Array.from(new Set(startDates));

    if (uniqueStartDates.length > 1) {
        return createFailure(`Start dates vary between orders`, "different-start-dates", FailureSeverity.Warning);
    }
};

const validateDiscardedItems = (
    items: ApproveOrderDeliveryItem[],
    selectedItemsCount: number
): ValidationFailure | void => {
    if (!items.length) {
        return createFailure(
            `${discardedOrdersInfoMessage}. Nothing to deliver.`,
            "no-orders-to-deliver",
            FailureSeverity.Error
        );
    } else if (items.length < selectedItemsCount) {
        return createFailure(discardedOrdersInfoMessage, "orders-discarded-from-delivery", FailureSeverity.Warning);
    }
};

const validateDeliveryEndpoints = (items: ApproveOrderDeliveryItem[]): ValidationFailure | void => {
    const deliveryEndpointIds = items.map((item) => item.deliveryEndpointId);
    const uniqueDeliveryEndpointIds = Array.from(new Set(deliveryEndpointIds));

    if (uniqueDeliveryEndpointIds.length > 1) {
        return createFailure(
            `Delivery endpoints vary between orders`,
            "different-delivery-endpoints",
            FailureSeverity.Error
        );
    }
};
